import { useContext } from 'react';
import UploadedImagesContext from '../../UploadedImagesContext';
import downsizeImage, { ImageResizeError } from './downsizeImage';
import type { UploadedImage } from '@/lib/types';
import api from '@/lib/api';
import * as Sentry from '@sentry/react';
import HttpMethod from '@/lib/constants/HttpMethod.enum';
import ApiError from '@/lib/api/ApiError';

const MAX_IMAGES_PER_FIELD = 20;

const useUploadImages = () => {
  const { images, imagesEndpoint, addImageToLocalState } = useContext(
    UploadedImagesContext,
  );

  const uploadImages = async (imageFiles: File[]): Promise<void> => {
    try {
      const formData = new FormData();

      const imagesToUpload = imageFiles.slice(
        0,
        MAX_IMAGES_PER_FIELD - images.length,
      );
      const downsizedImages = await Promise.all(
        imagesToUpload.map(downsizeImage),
      );

      for (const image of downsizedImages) {
        formData.append('images', image);
      }

      const imagesResponse = await api.call<Record<number, UploadedImage>>(
        imagesEndpoint,
        {
          method: HttpMethod.POST,
          body: formData,
        },
      );

      for (const image of Object.values(imagesResponse)) {
        addImageToLocalState(image);
      }
    } catch (error) {
      if (
        !(error instanceof ImageResizeError) &&
        !(error instanceof ApiError)
      ) {
        Sentry.captureException(error);

        return;
      }

      throw error;
    }
  };

  return uploadImages;
};

export default useUploadImages;
